import React, { Fragment, useEffect, useRef, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import { GetPharmaCompanies } from "../../../../api/medicalStore/master/pharmaCompanyAPI";
import { GetMedicineCompositions } from "../../../../api/medicalStore/master/medicineCompositionAPI";
import { GetMedicineGroups } from "../../../../api/medicalStore/master/medicineGroupAPI";
import { GetDepartments } from "../../../../api/masters/departmentAPI";
import { StoreItem } from "../../../../api/medicalStore/master/itemAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

/* ================= VALIDATION ================= */
const schema = yup.object().shape({
  name: yup.string().required(),
  medicineCompanyId: yup.string().required(),
  medicineCompositionId: yup.string().required(),
  medicineGroupId: yup.string().required(),
  restrictedGroupId: yup.string().required(),
  supplier: yup.string().required(),

  sgstAccount: yup.string().required(),
  cgstAccount: yup.string().required(),
  igstAccount: yup.string().required(),
  utgstAccount: yup.string().required(),

  hsnCode: yup.string().required(),
  unit: yup.number().required(),
  bin: yup.string().required(),

  minLevel: yup.number().required(),
  maxLevel: yup.number().required(),
  reOrderLevel: yup.number().required(),

  unitPacking: yup.string().required(),
  restricted: yup.string().required(),
  blockMrpEdit: yup.string().required(),
  blockRateEdit: yup.string().required(),

  gstPercentage: yup.number().required(),
  active: yup.string().required(),

  medicineDetails: yup.array().of(
    yup.object().shape({
      expiryDate: yup.date().required(),
      batchNo: yup.string().required(),
      packingQty: yup.number().required(),
      purchaseRate: yup.number().required(),
      amount: yup.number().required(),
      quantity: yup.number().required(),
      freeQuantity: yup.number().required(),
      mrp: yup.number().required(),
      saleRate: yup.number().required(),
      departmentId: yup.string().required()
    })
  )
});

const AddItem = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [companies, setCompanies] = useState([]);
  const [compositions, setCompositions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [departments, setDepartments] = useState([]);

  const { register, handleSubmit, control, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      medicineDetails: [{}]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "medicineDetails"
  });

  /* ================= LOAD MASTER DATA ================= */
  useEffect(() => {
    GetPharmaCompanies().then(r => r.success && setCompanies(r.data));
    GetMedicineCompositions().then(r => r.success && setCompositions(r.data));
    GetMedicineGroups().then(r => r.success && setGroups(r.data));
    GetDepartments().then(r => r.success && setDepartments(r.data));
  }, []);

  /* ================= SUBMIT ================= */
  const onSubmit = async data => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await StoreItem(data);
      if (response.success) {
        showToast("success", response.message);
        navigate(`/medical-store/master/item/list`);
      } else {
        showToast("error", response.message);
      }
    } catch (e) {
      showToast("error", e.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
  useKeyboardSubmit(handleSubmit, onSubmit);
  useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Medical Store" title="Add Item" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Add Item</h5>
            <Link to={`/medical-store/master/item/list`}>
              <button className="btn btn-primary" id="list">
                List Items
              </button>
              <UncontrolledTooltip target="list">
                List Items
              </UncontrolledTooltip>
            </Link>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="form theme-form">
            <div className="card-body">
              <div className="row">
                {/* ================= BASIC INFORMATION ================= */}
                <div className="col-12">
                  <h6 className="mb-3">Basic Information</h6>
                </div>

                <div className="col-md-6">
                  <label>Item Name</label>
                  <input className="form-control" {...register("name")} />
                  {errors.name &&
                    <span className="text-danger">
                      {errors.name.message}
                    </span>}
                </div>

                <div className="col-md-6">
                  <label>Supplier</label>
                  <input className="form-control" {...register("supplier")} />
                  {errors.supplier &&
                    <span className="text-danger">
                      {errors.supplier.message}
                    </span>}
                </div>

                {/* ================= MASTER REFERENCES ================= */}
                <div className="col-12 mt-4">
                  <h6 className="mb-3">Master References</h6>
                </div>

                <div className="col-md-6">
                  <label>Company</label>
                  <select
                    className="form-control"
                    {...register("medicineCompanyId")}
                  >
                    <option value="">Select Company</option>
                    {companies.map(c =>
                      <option key={c._id} value={c._id}>
                        {c.name}
                      </option>
                    )}
                  </select>
                  {errors.medicineCompanyId &&
                    <span className="text-danger">
                      {errors.medicineCompanyId.message}
                    </span>}
                </div>

                <div className="col-md-6">
                  <label>Composition</label>
                  <select
                    className="form-control"
                    {...register("medicineCompositionId")}
                  >
                    <option value="">Select Composition</option>
                    {compositions.map(c =>
                      <option key={c._id} value={c._id}>
                        {c.name}
                      </option>
                    )}
                  </select>
                  {errors.medicineCompositionId &&
                    <span className="text-danger">
                      {errors.medicineCompositionId.message}
                    </span>}
                </div>

                <div className="col-md-6">
                  <label>Medicine Group</label>
                  <select
                    className="form-control"
                    {...register("medicineGroupId")}
                  >
                    <option value="">Select Group</option>
                    {groups.map(g =>
                      <option key={g._id} value={g._id}>
                        {g.name}
                      </option>
                    )}
                  </select>
                  {errors.medicineGroupId &&
                    <span className="text-danger">
                      {errors.medicineGroupId.message}
                    </span>}
                </div>

                <div className="col-md-6">
                  <label>Restricted Group</label>
                  <select
                    className="form-control"
                    {...register("restrictedGroupId")}
                  >
                    <option value="">Select Restricted Group</option>
                    {groups.map(g =>
                      <option key={g._id} value={g._id}>
                        {g.name}
                      </option>
                    )}
                  </select>
                  {errors.restrictedGroupId &&
                    <span className="text-danger">
                      {errors.restrictedGroupId.message}
                    </span>}
                </div>

                {/* ================= TAX ACCOUNTS ================= */}
                <div className="col-12 mt-4">
                  <h6 className="mb-3">GST Accounts</h6>
                </div>

                <div className="col-md-3">
                  <label>SGST Account</label>
                  <input
                    className="form-control"
                    {...register("sgstAccount")}
                  />
                  {errors.sgstAccount &&
                    <span className="text-danger">
                      {errors.sgstAccount.message}
                    </span>}
                </div>

                <div className="col-md-3">
                  <label>CGST Account</label>
                  <input
                    className="form-control"
                    {...register("cgstAccount")}
                  />
                  {errors.cgstAccount &&
                    <span className="text-danger">
                      {errors.cgstAccount.message}
                    </span>}
                </div>

                <div className="col-md-3">
                  <label>IGST Account</label>
                  <input
                    className="form-control"
                    {...register("igstAccount")}
                  />
                  {errors.igstAccount &&
                    <span className="text-danger">
                      {errors.igstAccount.message}
                    </span>}
                </div>

                <div className="col-md-3">
                  <label>UTGST Account</label>
                  <input
                    className="form-control"
                    {...register("utgstAccount")}
                  />
                  {errors.utgstAccount &&
                    <span className="text-danger">
                      {errors.utgstAccount.message}
                    </span>}
                </div>

                {/* ================= INVENTORY DETAILS ================= */}
                <div className="col-12 mt-4">
                  <h6 className="mb-3">Inventory & Tax Details</h6>
                </div>

                <div className="col-md-3">
                  <label>HSN Code</label>
                  <input className="form-control" {...register("hsnCode")} />
                  {errors.hsnCode &&
                    <span className="text-danger">
                      {errors.hsnCode.message}
                    </span>}
                </div>

                <div className="col-md-3">
                  <label>GST Percentage</label>
                  <input
                    type="number"
                    className="form-control"
                    {...register("gstPercentage")}
                  />
                  {errors.gstPercentage &&
                    <span className="text-danger">
                      {errors.gstPercentage.message}
                    </span>}
                </div>

                <div className="col-md-3">
                  <label>Unit</label>
                  <input
                    type="number"
                    className="form-control"
                    {...register("unit")}
                  />
                  {errors.unit &&
                    <span className="text-danger">
                      {errors.unit.message}
                    </span>}
                </div>

                <div className="col-md-3">
                  <label>Unit Packing</label>
                  <input
                    className="form-control"
                    {...register("unitPacking")}
                  />
                  {errors.unitPacking &&
                    <span className="text-danger">
                      {errors.unitPacking.message}
                    </span>}
                </div>

                <div className="col-md-3">
                  <label>Bin</label>
                  <input className="form-control" {...register("bin")} />
                  {errors.bin &&
                    <span className="text-danger">
                      {errors.bin.message}
                    </span>}
                </div>

                <div className="col-md-3">
                  <label>Min Level</label>
                  <input
                    type="number"
                    className="form-control"
                    {...register("minLevel")}
                  />
                  {errors.minLevel &&
                    <span className="text-danger">
                      {errors.minLevel.message}
                    </span>}
                </div>

                <div className="col-md-3">
                  <label>Max Level</label>
                  <input
                    type="number"
                    className="form-control"
                    {...register("maxLevel")}
                  />
                  {errors.maxLevel &&
                    <span className="text-danger">
                      {errors.maxLevel.message}
                    </span>}
                </div>

                <div className="col-md-3">
                  <label>Re-Order Level</label>
                  <input
                    type="number"
                    className="form-control"
                    {...register("reOrderLevel")}
                  />
                  {errors.reOrderLevel &&
                    <span className="text-danger">
                      {errors.reOrderLevel.message}
                    </span>}
                </div>

                {/* ================= FLAGS ================= */}
                <div className="col-12 mt-4">
                  <h6 className="mb-3">Flags & Status</h6>
                </div>

                <div className="col-md-3">
                  <label>Restricted</label>
                  <select className="form-control" {...register("restricted")}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors.restricted &&
                    <span className="text-danger">
                      {errors.restricted.message}
                    </span>}
                </div>

                <div className="col-md-3">
                  <label>Block MRP Edit</label>
                  <select
                    className="form-control"
                    {...register("blockMrpEdit")}
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors.blockMrpEdit &&
                    <span className="text-danger">
                      {errors.blockMrpEdit.message}
                    </span>}
                </div>

                <div className="col-md-3">
                  <label>Block Rate Edit</label>
                  <select
                    className="form-control"
                    {...register("blockRateEdit")}
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors.blockRateEdit &&
                    <span className="text-danger">
                      {errors.blockRateEdit.message}
                    </span>}
                </div>

                <div className="col-md-3">
                  <label>Active</label>
                  <select className="form-control" {...register("active")}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors.active &&
                    <span className="text-danger">
                      {errors.active.message}
                    </span>}
                </div>

                {/* ================= MEDICINE DETAILS ================= */}
                <div className="col-12 mt-4">
                  <h6 className="mb-3">Medicine Batch Details</h6>

                  {fields.map((item, index) =>
                    <div className="row border p-3 mb-3" key={item.id}>
                      <div className="col-md-3">
                        <label>Expiry Date</label>
                        <input
                          type="date"
                          className="form-control"
                          {...register(`medicineDetails.${index}.expiryDate`)}
                        />
                      </div>

                      <div className="col-md-3">
                        <label>Batch No</label>
                        <input
                          className="form-control"
                          {...register(`medicineDetails.${index}.batchNo`)}
                        />
                      </div>

                      <div className="col-md-3">
                        <label>Packing Qty</label>
                        <input
                          type="number"
                          className="form-control"
                          {...register(`medicineDetails.${index}.packingQty`)}
                        />
                      </div>

                      <div className="col-md-3">
                        <label>Purchase Rate</label>
                        <input
                          type="number"
                          className="form-control"
                          {...register(`medicineDetails.${index}.purchaseRate`)}
                        />
                      </div>

                      <div className="col-md-3">
                        <label>Amount</label>
                        <input
                          type="number"
                          className="form-control"
                          {...register(`medicineDetails.${index}.amount`)}
                        />
                      </div>

                      <div className="col-md-3">
                        <label>Quantity</label>
                        <input
                          type="number"
                          className="form-control"
                          {...register(`medicineDetails.${index}.quantity`)}
                        />
                      </div>

                      <div className="col-md-3">
                        <label>Free Quantity</label>
                        <input
                          type="number"
                          className="form-control"
                          {...register(`medicineDetails.${index}.freeQuantity`)}
                        />
                      </div>

                      <div className="col-md-3">
                        <label>MRP</label>
                        <input
                          type="number"
                          className="form-control"
                          {...register(`medicineDetails.${index}.mrp`)}
                        />
                      </div>

                      <div className="col-md-3">
                        <label>Sale Rate</label>
                        <input
                          type="number"
                          className="form-control"
                          {...register(`medicineDetails.${index}.saleRate`)}
                        />
                      </div>

                      <div className="col-md-3">
                        <label>Department</label>
                        <select
                          className="form-control"
                          {...register(`medicineDetails.${index}.departmentId`)}
                        >
                          <option value="">Select Department</option>
                          {departments.map(d =>
                            <option key={d._id} value={d._id}>
                              {d.name}
                            </option>
                          )}
                        </select>
                      </div>

                      <div className="col-md-1 d-flex align-items-end">
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => remove(index)}
                        >
                          X
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => append({})}
                  >
                    + Add Batch
                  </button>
                </div>
              </div>
            </div>

            <div className="card-footer text-end">
              <button className="btn btn-primary" type="submit">
                Save Item
              </button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default AddItem;
